import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#faf6f0]/95 backdrop-blur-md shadow-sm' : 'bg-[#faf6f0]/70 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            onClick={() => {
              setOpen(false);
              // Always scroll to top when logo is clicked (even if already on / )
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }}
            aria-label="RCD - Home"
          >
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/jboyka5i_RCD%20logo.jpg"
              alt="RCD - Rajdhani Crafts and Daughters"
              data-testid="navbar-logo"
              className="h-16 md:h-20 lg:h-[88px] w-auto object-contain transition-transform duration-500 group-hover:scale-105"
              style={{ mixBlendMode: 'multiply', filter: 'brightness(1.03) contrast(1.06)' }}
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl md:text-3xl font-semibold tracking-tight text-[#1a1614] group-hover:text-[#a67c3d] transition-colors">
                RCD
              </span>
              <span className="text-[10px] md:text-[11px] tracking-[0.24em] text-[#6b5847] uppercase mt-1.5">
                Rajdhani Crafts & Daughters
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-11 xl:gap-12">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              Home
            </NavLink>

            {/* About — single link, no dropdown */}
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              About
            </NavLink>

            <NavLink
              to="/products"
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              Products
            </NavLink>

            <NavLink
              to="/our-plant"
              data-testid="nav-our-plant"
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              Our Plant
            </NavLink>

            <NavLink
              to="/trade-fairs"
              data-testid="nav-trade-fairs"
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              Trade Fairs
            </NavLink>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `text-[15px] xl:text-base tracking-wide font-medium rcd-link ${
                  isActive ? 'text-[#a67c3d]' : 'text-[#1a1614] hover:text-[#a67c3d]'
                }`
              }
            >
              Contact Us
            </NavLink>
          </nav>

          <button
            aria-label="Menu"
            className="lg:hidden p-2 text-[#1a1614]"
            onClick={() => setOpen(o => !o)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#faf6f0] border-t border-[#e8dfd1]">
          <div className="px-6 py-6 flex flex-col gap-1">
            <NavLink
              to="/"
              end
              onClick={() => setOpen(false)}
              className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}
            >
              Home
            </NavLink>

            <div className="h-px bg-[#e8dfd1] my-2" />

            <NavLink to="/about" onClick={() => setOpen(false)} className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}>About</NavLink>

            <div className="h-px bg-[#e8dfd1] my-3" />

            <NavLink to="/products" onClick={() => setOpen(false)} className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}>Products</NavLink>
            <NavLink to="/our-plant" onClick={() => setOpen(false)} className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}>Our Plant</NavLink>
            <NavLink to="/trade-fairs" onClick={() => setOpen(false)} className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}>Trade Fairs</NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} className={({ isActive }) => `py-2 text-base font-medium ${isActive ? 'text-[#a67c3d]' : 'text-[#1a1614]'}`}>Contact Us</NavLink>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
